import React from 'react';

import { DEFAULT_FAVORITES_LIST } from '@smart-tv/contexts/favorites-lists-context/favorites-lists-context';
import useFavoritesLists from '@smart-tv/hooks/use-favorites-lists';
import { TvGuideChannel, TvGuideProgram } from '@smart-tv/pages/tv-guide/types';

import getElapsedMinutesOfTheDay from '@smart-tv/utils/date/get-elapsed-minutes-of-day';
import getTimeFormatted from '@smart-tv/utils/date/get-time-formatted';
import { IPositionProps } from '@smart-tv/utils/is-in-view/is-in-view';

import TvGuideContext from '@smart-tv/pages/tv-guide/utils/tv-guide-context';
import { getTvGuideScale, getScaledConstants, ScaledTvGuideConstants } from './tv-guide-helpers';

export interface TvGuideDayViewTime {
  minutes: number;
  formatted: string | undefined;
  unix: number;
}

export interface TvGuideDayViewContextProps {
  children: React.ReactNode | React.ReactNode[];
}

export interface TvGuideDayViewContextValues {
  scroll: IPositionProps;
  setScroll: React.Dispatch<React.SetStateAction<IPositionProps>>;
  time: TvGuideDayViewTime;
  setTime: React.Dispatch<React.SetStateAction<TvGuideDayViewTime>>;
  activeProgram: TvGuideProgram;
  setActiveProgram: React.Dispatch<React.SetStateAction<TvGuideProgram>>;
  activeCenter: number | undefined;
  setActiveCenter: React.Dispatch<React.SetStateAction<number | undefined>>;
  activeChannelIndex: number;
  setActiveChannelIndex: React.Dispatch<React.SetStateAction<number>>;
  scrollXRef: React.RefObject<HTMLDivElement>;
  scrollYRef: React.RefObject<HTMLDivElement>;
  activeTVGuideFocusKey: string | undefined;
  setActiveTVGuideFocusKey: React.Dispatch<React.SetStateAction<string | undefined>>;
  gridFocused: boolean;
  setGridFocused: React.Dispatch<React.SetStateAction<boolean>>;
  verticalArrowPress: boolean;
  setVerticalArrowPress: React.Dispatch<React.SetStateAction<boolean>>;
  isFavoritesListSelected: boolean;
  setIsFavoritesListSelected: React.Dispatch<React.SetStateAction<boolean>>;
  resetFocusAnchors: () => void;
  scale: number;
  scaledConstants: ScaledTvGuideConstants;
}

export const TvGuideDayViewContext = React.createContext<TvGuideDayViewContextValues>(
  {} as TvGuideDayViewContextValues
);

export const TvGuideDayViewConsumer = TvGuideDayViewContext.Consumer;

export const TvGuideDayViewProvider = (props: TvGuideDayViewContextProps): JSX.Element => {
  const { children } = props;

  const { channels, selectedDate, isChannelsLoading } = React.useContext(TvGuideContext);
  const { selectedFavoritesList } = useFavoritesLists();

  const favoritesResetKey = `${selectedFavoritesList?.id ?? ''}:${
    selectedFavoritesList?.sids?.join(',') ?? ''
  }`;

  const getFirstProgramInProgress = (
    unixTime: number,
    channels?: TvGuideChannel[]
  ): TvGuideProgram => {
    if (channels) {
      const activeProgramOnFirstChannel = channels[0].programs.find(
        (program) => program.startTime < unixTime && program.endTime > unixTime
      );

      return activeProgramOnFirstChannel || channels[0].programs[0];
    }
    return {} as TvGuideProgram;
  };

  const scrollXRef = React.useRef<HTMLDivElement>(null);
  const scrollYRef = React.useRef<HTMLDivElement>(null);

  // We keep viewport width in state so scale can be derived declaratively.
  // This gives us one stable source that all TV guide consumers can subscribe to.
  const [viewportWidth, setViewportWidth] = React.useState(() => window.innerWidth);

  // Resize can fire many times while the user drags the browser edge.
  // Debouncing avoids rapid intermediate rerenders and visible jitter.
  // TV devices practically never resize, but browser mode does.
  React.useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setViewportWidth(window.innerWidth), 150);
    };
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);
  // Step 1: derive clamped scale from viewport width.
  const scale = React.useMemo(() => getTvGuideScale(viewportWidth), [viewportWidth]);

  // Step 2: derive all geometric constants from the same scale.
  // This guarantees the timeline, channels, rows, and program cards stay aligned.
  const scaledConstants = React.useMemo(() => getScaledConstants(scale), [scale]);

  // Keep track of the previous scale's coordinate units so we can remap
  // existing scroll offsets onto the new coordinate system.
  const prevMinuteWidthRef = React.useRef<number>(scaledConstants.minuteWidth);
  const prevRowStrideRef = React.useRef<number>(
    scaledConstants.programHeight + scaledConstants.programGap
  );

  // Important: after scale changes, CSS updates dimensions immediately but no native
  // scroll event is emitted. Also, old scrollLeft/scrollTop are expressed in the
  // previous pixel scale. To preserve the same timeline moment/row in view, we first
  // remap offsets to the new scale and only then sync scroll state.
  React.useEffect(() => {
    const x = scrollXRef.current;
    const y = scrollYRef.current;
    const prevMinuteWidth = prevMinuteWidthRef.current;
    const nextMinuteWidth = scaledConstants.minuteWidth;
    const prevRowStride = prevRowStrideRef.current;
    const nextRowStride = scaledConstants.programHeight + scaledConstants.programGap;

    if (!x && !y) {
      prevMinuteWidthRef.current = nextMinuteWidth;
      prevRowStrideRef.current = nextRowStride;
      return;
    }

    if (x && prevMinuteWidth > 0 && nextMinuteWidth > 0) {
      // Preserve the timeline moment at the VIEWPORT CENTER (not just left edge):
      // 1) find which minute is currently at center in old scale,
      // 2) map that same minute into new scale,
      // 3) set scrollLeft so it lands back at center.
      const minuteAtCenter = (x.scrollLeft + x.clientWidth / 2) / prevMinuteWidth;
      const targetLeft = minuteAtCenter * nextMinuteWidth - x.clientWidth / 2;
      const maxLeft = Math.max(0, x.scrollWidth - x.clientWidth);
      x.scrollLeft = Math.min(Math.max(targetLeft, 0), maxLeft);
    }

    if (y && prevRowStride > 0 && nextRowStride > 0) {
      // Preserve the vertically visible row position across height scaling.
      const rowAnchor = y.scrollTop / prevRowStride;
      y.scrollTop = rowAnchor * nextRowStride;
    }

    setScroll({
      left: x?.scrollLeft ?? 0,
      right: (x?.scrollLeft ?? 0) + (x?.clientWidth ?? 0),
      top: y?.scrollTop ?? 0,
      bottom: (y?.scrollTop ?? 0) + (y?.clientHeight ?? 0)
    });

    prevMinuteWidthRef.current = nextMinuteWidth;
    prevRowStrideRef.current = nextRowStride;
  }, [scaledConstants.minuteWidth, scaledConstants.programHeight, scaledConstants.programGap]);

  const [scroll, setScroll] = React.useState<IPositionProps>({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  });

  const [time, setTime] = React.useState({
    minutes: getElapsedMinutesOfTheDay(),
    formatted: getTimeFormatted(),
    unix: new Date().valueOf() / 1000
  });

  const [activeProgram, setActiveProgram] = React.useState<TvGuideProgram>(
    getFirstProgramInProgress(time.unix, channels)
  );
  const [activeCenter, setActiveCenter] = React.useState<number>();
  const [activeTVGuideFocusKey, setActiveTVGuideFocusKey] = React.useState<string | undefined>();
  const [activeChannelIndex, setActiveChannelIndex] = React.useState<number>(0);
  const [gridFocused, setGridFocused] = React.useState(false);
  const [verticalArrowPress, setVerticalArrowPress] = React.useState(true);
  const [isFavoritesListSelected, setIsFavoritesListSelected] = React.useState(() =>
    Boolean(selectedFavoritesList && selectedFavoritesList.id !== DEFAULT_FAVORITES_LIST.id)
  );

  React.useEffect(() => {
    const hasCustomFavoritesList = Boolean(
      selectedFavoritesList && selectedFavoritesList.id !== DEFAULT_FAVORITES_LIST.id
    );
    setIsFavoritesListSelected(hasCustomFavoritesList);
  }, [selectedFavoritesList]);

  // ── Selection reconciliation ────────────────────────────────────────────────

  const pendingFavoritesResetKeyRef = React.useRef<string | null>(null);
  const didSeeLoadingForPendingFavoritesResetRef = React.useRef(false);
  const initializedFavoritesResetKeyRef = React.useRef(false);

  const resetFocusAnchors = React.useCallback(() => {
    setActiveTVGuideFocusKey(undefined);
    setActiveCenter(undefined);
  }, []);

  // Track when the favorites key changes (skip the very first call — that's the initial load)
  React.useEffect(() => {
    if (pendingFavoritesResetKeyRef.current !== favoritesResetKey) {
      pendingFavoritesResetKeyRef.current = favoritesResetKey;
      didSeeLoadingForPendingFavoritesResetRef.current = false;
      if (!initializedFavoritesResetKeyRef.current) {
        initializedFavoritesResetKeyRef.current = true;
        return;
      }
    }
  }, [favoritesResetKey]);

  // Observe the loading cycle triggered by the favorites change
  React.useEffect(() => {
    if (pendingFavoritesResetKeyRef.current === favoritesResetKey && isChannelsLoading) {
      didSeeLoadingForPendingFavoritesResetRef.current = true;
    }
  }, [favoritesResetKey, isChannelsLoading]);

  // Reconcile activeProgram when channels data is updated
  React.useEffect(() => {
    if (!channels?.length || isChannelsLoading) return;

    const now = new Date();
    const selectedAtCurrentTime = new Date(selectedDate.date);
    selectedAtCurrentTime.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
    const unixTime = selectedAtCurrentTime.valueOf() / 1000;

    const getFirstChannelProgram = () => {
      const first = channels[0];
      return (
        first.programs.find((p) => p.startTime < unixTime && p.endTime > unixTime) ??
        first.programs[0]
      );
    };

    // Favorites edits → reinitialize like a fresh visit
    if (
      pendingFavoritesResetKeyRef.current === favoritesResetKey &&
      didSeeLoadingForPendingFavoritesResetRef.current
    ) {
      pendingFavoritesResetKeyRef.current = null;
      didSeeLoadingForPendingFavoritesResetRef.current = false;

      setActiveProgram(getFirstChannelProgram());
      setActiveChannelIndex(0);
      resetFocusAnchors();
      return;
    }

    // Same channel found → preserve or date-switch within same channel
    if (activeProgram?.sid) {
      const channel = channels.find((ch) => ch.sid === activeProgram.sid);
      if (channel) {
        const updatedProgram = channel.programs.find((p) => p.eventId === activeProgram.eventId);
        if (updatedProgram) {
          setActiveProgram(updatedProgram);
          return;
        }
        // Date switch fallback: same channel, find program at current time of day
        const programAtCurrentTime = channel.programs.find(
          (p) => p.startTime < unixTime && p.endTime > unixTime
        );
        setActiveProgram(programAtCurrentTime ?? channel.programs[0]);
        return;
      }
    }

    // Channel list switch: SID not found → reset to top of new list
    setActiveProgram(getFirstChannelProgram());
    setActiveChannelIndex(0);
  }, [
    activeProgram,
    channels,
    favoritesResetKey,
    isChannelsLoading,
    resetFocusAnchors,
    selectedDate.date
  ]);

  return (
    <TvGuideDayViewContext.Provider
      value={{
        scroll,
        setScroll,
        time,
        setTime,
        activeProgram,
        setActiveProgram,
        activeCenter,
        setActiveCenter,
        activeChannelIndex,
        setActiveChannelIndex,
        scrollXRef,
        scrollYRef,
        activeTVGuideFocusKey,
        setActiveTVGuideFocusKey,
        gridFocused,
        setGridFocused,
        verticalArrowPress,
        setVerticalArrowPress,
        isFavoritesListSelected,
        setIsFavoritesListSelected,
        resetFocusAnchors,
        scale,
        scaledConstants
      }}
    >
      {children}
    </TvGuideDayViewContext.Provider>
  );
};

export default TvGuideDayViewContext;
